import React, { useEffect, useMemo, useState } from "react";
import {
  BottomSheet,
  Button,
  DropdownField,
  Modal,
  MultiSelectField,
  TextField,
  useIsMobile,
} from "../../../components/ui";
import {
  BrandPlan,
  NewBrandPlan,
  PlanDuration,
  SUPPORT_CHANNELS,
  SupportChannel,
} from "../types/brand.types";
import CustomNameField from "./CustomNameField";

/** Lets the footer's submit button live outside the `<form>` element. */
const FORM_ID = "plan-form";

interface PlanFormModalProps {
  open: boolean;
  /** When set, the dialog edits this plan instead of creating one. */
  plan?: BrandPlan | null;
  /**
   * The names the backend suggests. Suggestions only — the list is never all
   * of them, which is what the "Add custom plan" row under it is for.
   */
  suggestions: string[];
  /**
   * The lengths a plan can be sold for. This list *is* all of them.
   *
   * Whole records rather than labels, because a save sends the id: the form is
   * filled in by label, so this is what turns the one back into the other.
   */
  durations: PlanDuration[];
  /** True while the save is with the server; the submit button reads it. */
  saving?: boolean;
  onClose: () => void;
  onSubmit: (plan: NewBrandPlan) => void;
}

/**
 * A count as the form holds it while it is being typed, and as the save sends
 * it. Blank is `null` and not 0 — a plan that does not say how many
 * consultations it includes is not one that includes none.
 */
const toCount = (value: string): number | null =>
  value.trim() === "" ? null : Number(value);

const fromCount = (count: number | null | undefined): string =>
  count === null || count === undefined ? "" : String(count);

const PlanFormModal: React.FC<PlanFormModalProps> = ({
  open,
  plan,
  suggestions,
  durations,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("");
  // Kept as a string while typing so the field can be empty; only the digits
  // are stored, which keeps the ₹ prefix from ever landing in the value.
  const [amount, setAmount] = useState("");
  // The three "how many" fields, kept as strings for the reason the amount is:
  // each one can be left empty, and empty is an answer the backend takes.
  const [consultations, setConsultations] = useState("");
  const [mealPlans, setMealPlans] = useState("");
  const [mealOptions, setMealOptions] = useState("");
  const [channels, setChannels] = useState<SupportChannel[]>([]);
  // While set, the name is typed rather than picked. Entered through the list's
  // "Add custom plan" row, and left by the chevron beside the box.
  const [custom, setCustom] = useState(false);

  // Which shell the form is put in. Not a `md:` class: a centred dialog and a
  // sheet that comes up from the bottom edge are two different elements with
  // two different ways in and out, not one panel with different corners.
  const isMobile = useIsMobile();

  const editing = !!plan;

  // Every open starts from the plan being edited, or from a blank form —
  // never from the last attempt.
  useEffect(() => {
    if (!open) return;
    setName(plan?.name ?? "");
    setDuration(plan?.duration ?? "");
    setAmount(plan ? String(plan.price) : "");
    setConsultations(fromCount(plan?.consultations));
    setMealPlans(fromCount(plan?.mealPlans));
    setMealOptions(fromCount(plan?.mealOptionsPerMeal));
    // Copied rather than shared: ticking a channel must not reach back into the
    // row the table is still showing.
    setChannels(plan ? [...plan.supportChannels] : []);
    // A plan named something the suggestions do not hold was typed once
    // already, so editing it opens back in the box it was typed into — the
    // dropdown would have nothing to show for it but the placeholder.
    setCustom(!!plan && !suggestions.includes(plan.name));
    // `suggestions` is deliberately not a dependency: this decides how the
    // dialog opens, and a list that arrives afterwards must not reach in and
    // swap the control out from under whoever is filling it in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan]);

  const durationLabels = useMemo(
    () => durations.map((option) => option.label),
    [durations]
  );

  // The form holds the label, but the save sends the id. A label with no
  // record behind it is a duration the backend has since renamed or dropped,
  // and there is nothing to send for it — so it does not count as filled in.
  const picked = durations.find((option) => option.label === duration);

  const price = Number(amount);
  const valid = !!name.trim() && !!picked && price > 0;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || !picked || saving) return;
    onSubmit({
      name: name.trim(),
      duration: picked.label,
      durationId: picked.id,
      price,
      consultations: toCount(consultations),
      mealPlans: toCount(mealPlans),
      mealOptionsPerMeal: toCount(mealOptions),
      supportChannels: channels,
    });
  };

  const title = editing ? "Edit plan" : "Create new plan";

  /** The same two buttons whichever shell is drawn — the sheet pins them under
   *  its body, the dialog puts them in its footer row. */
  const actions = (
    <>
      <Button
        variant="secondary"
        size="md"
        onClick={onClose}
        // A save is already with the server; cancelling would leave the
        // dialog gone and the row about to arrive with nothing to land in.
        disabled={saving}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        size="md"
        form={FORM_ID}
        disabled={!valid}
        loading={saving}
      >
        {editing ? "Save changes" : "Create plan"}
      </Button>
    </>
  );

  const form = (
    <form id={FORM_ID} onSubmit={handleSubmit}>
      {custom ? (
        <CustomNameField
          label="Write the plan name"
          value={name}
          placeholder="Write the plan name"
          dismissLabel="Pick from the suggested plans"
          onValueChange={setName}
          // Back to the list, and empty: the box is left because what was
          // typed into it is not what was wanted.
          onDismiss={() => {
            setCustom(false);
            setName("");
          }}
        />
      ) : (
        <DropdownField
          label="Write the plan name"
          value={name}
          placeholder="Select plan"
          options={suggestions}
          onValueChange={setName}
          // The list grows with the backend's, and a dietitian who knows
          // what they are looking for should not have to scroll for it.
          searchable
          footerAction={{
            label: "Add custom plan",
            onSelect: (typed) => {
              setCustom(true);
              // Carried over rather than cleared: the box the row swaps in
              // sits where this one was, and text that vanished as the
              // control changed under the cursor would read as a bug —
              // never mind that a filter which matched nothing is exactly
              // the name being typed.
              setName(typed);
            },
          }}
        />
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DropdownField
          label="Select duration"
          value={duration}
          placeholder="Select duration"
          options={durationLabels}
          onValueChange={setDuration}
        />
        <AmountField value={amount} onValueChange={setAmount} />

        {/* What the plan comes with. All four are optional: plenty of
            plans are sold on a name, a length and a price alone, and the
            submit button does not wait on any of these. */}
        <CountField
          label="Consultations"
          value={consultations}
          placeholder="4"
          onValueChange={setConsultations}
        />
        <CountField
          label="Meal plans"
          value={mealPlans}
          placeholder="2"
          onValueChange={setMealPlans}
        />
        <CountField
          label="Meal options per meal"
          value={mealOptions}
          placeholder="3"
          onValueChange={setMealOptions}
        />

        <MultiSelectField
          label="Support channels"
          values={channels}
          placeholder="Select channels"
          // Whole options rather than plain strings: the field shows
          // "Whatsapp" and holds the `WHATSAPP` the backend stores.
          options={SUPPORT_CHANNELS}
          onValuesChange={(values) =>
            setChannels(values as SupportChannel[])
          }
        />
      </div>
    </form>
  );

  // A phone gets the sheet: three fields and two buttons is a small form, and
  // a small form is easier to fill in from the bottom edge — where the thumb
  // already is and where the keyboard is about to come up — than from a panel
  // floating in the middle of the screen with the page dimmed around it. The
  // fields are the same either way; only what holds them changes. The two
  // dropdowns inside open over the sheet rather than inside it, so a long list
  // of plan names is not folded into what little of the sheet is left.
  if (isMobile) {
    return (
      <BottomSheet open={open} title={title} onClose={onClose} footer={actions}>
        <div className="px-5 pb-2 pt-2">{form}</div>
      </BottomSheet>
    );
  }

  return (
    <Modal open={open} title={title} onClose={onClose} footer={actions}>
      {form}
    </Modal>
  );
};

/**
 * One of the three "how many" fields. Unlike the amount beside it these have
 * nothing painted inside the control, so they are the kit's own `TextField`
 * with the same digits-only filter over the top — a whole number of
 * consultations is the only thing any of them can be.
 */
const CountField: React.FC<{
  label: string;
  value: string;
  placeholder: string;
  onValueChange: (value: string) => void;
}> = ({ label, value, placeholder, onValueChange }) => (
  <TextField
    label={label}
    value={value}
    placeholder={placeholder}
    inputMode="numeric"
    onValueChange={(raw) => onValueChange(raw.replace(/[^0-9]/g, ""))}
  />
);

/**
 * The price input. It gets its own component rather than reusing `TextField`
 * because of the ₹ sitting inside the control, which needs the padding and the
 * adornment to line up.
 */
const AmountField: React.FC<{
  value: string;
  onValueChange: (value: string) => void;
}> = ({ value, onValueChange }) => {
  const fieldId = React.useId();

  return (
    <div>
      <label
        htmlFor={fieldId}
        className="mb-1.5 block text-[13px] 3xl:text-[14px] text-ink"
      >
        Amount
      </label>
      <div className="relative">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] 3xl:text-[15px] text-ink-subtle"
        >
          ₹
        </span>
        <input
          id={fieldId}
          // `inputMode` keeps the numeric keypad on mobile; a `number` input
          // would add spinners the design doesn't have.
          type="text"
          inputMode="numeric"
          value={value}
          placeholder="499"
          onChange={(event) =>
            onValueChange(event.target.value.replace(/[^0-9]/g, ""))
          }
          className="w-full rounded-[6px] border border-line bg-white py-2.5 3xl:py-3 pl-8 pr-3.5 text-[14px] 3xl:text-[15px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand transition-colors"
        />
      </div>
    </div>
  );
};

export default PlanFormModal;
